/**
 * Input Component
 *
 * A flexible input field component built with Community Trust design tokens.
 * Supports labels, help text, error states, and icons.
 */

import React from 'react';
import { clsx } from 'clsx';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Label text for the input
   */
  label?: string;

  /**
   * Helper text displayed below the input
   */
  helperText?: string;

  /**
   * Error message (displays input in error state)
   */
  error?: string;

  /**
   * Success message (displays input in success state)
   */
  success?: string;

  /**
   * Input size
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Whether the input should take full width
   */
  fullWidth?: boolean;

  /**
   * Icon to display before input
   */
  leftIcon?: React.ReactNode;

  /**
   * Icon to display after input
   */
  rightIcon?: React.ReactNode;
}

/**
 * Input component using Community Trust design system
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      success,
      size = 'md',
      fullWidth = false,
      leftIcon,
      rightIcon,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputId = React.useId();

    // Container styles
    const containerStyles = clsx({
      'w-full': fullWidth,
      'inline-block': !fullWidth,
    });

    // Input wrapper styles (for icons)
    const wrapperStyles = clsx(
      'relative',
      {
        'w-full': fullWidth,
      }
    );

    // Base input styles
    const baseInputStyles = clsx(
      'border border-neutral-300 rounded-lg',
      'transition-all duration-base',
      'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-100',
      'placeholder:text-neutral-400',
      {
        'w-full': fullWidth,
      }
    );

    // State-specific styles
    const stateStyles = clsx({
      'border-error-500 focus:ring-error-500': error,
      'border-success-500 focus:ring-success-500': success && !error,
    });

    // Size styles
    const sizeStyles = {
      sm: 'px-3 py-1.5 text-sm',  // 12px padding, 6px vertical
      md: 'px-4 py-2 text-base',  // 16px padding, 8px vertical
      lg: 'px-5 py-3 text-lg',    // 20px padding, 12px vertical
    };

    // Icon padding adjustments
    const iconPadding = {
      left: {
        sm: 'pl-9',   // 36px (icon + spacing)
        md: 'pl-10',  // 40px
        lg: 'pl-12',  // 48px
      },
      right: {
        sm: 'pr-9',
        md: 'pr-10',
        lg: 'pr-12',
      },
    };

    // Icon size styles
    const iconSizeStyles = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    // Icon position styles
    const leftIconStyles = clsx(
      'absolute left-3 top-1/2 -translate-y-1/2',
      'text-neutral-400',
      iconSizeStyles[size]
    );

    const rightIconStyles = clsx(
      'absolute right-3 top-1/2 -translate-y-1/2',
      'text-neutral-400',
      iconSizeStyles[size]
    );

    return (
      <div className={containerStyles}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-700 mb-1"
          >
            {label}
          </label>
        )}

        {/* Input wrapper (for icons) */}
        <div className={wrapperStyles}>
          {/* Left icon */}
          {leftIcon && (
            <div className={leftIconStyles}>
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            className={clsx(
              baseInputStyles,
              stateStyles,
              sizeStyles[size],
              leftIcon && iconPadding.left[size],
              rightIcon && iconPadding.right[size],
              className
            )}
            {...props}
          />

          {/* Right icon */}
          {rightIcon && (
            <div className={rightIconStyles}>
              {rightIcon}
            </div>
          )}
        </div>

        {/* Helper/Error/Success text */}
        {(helperText || error || success) && (
          <p
            className={clsx(
              'mt-1 text-sm',
              {
                'text-neutral-600': !error && !success,
                'text-error-600': error,
                'text-success-600': success && !error,
              }
            )}
          >
            {error || success || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
